import { type RefObject, useEffect, useRef } from 'react';
import { useTvNavigation } from './navigation';

/**
 * Handles Back while mounted.
 *
 * Handlers stack, so a dialog's handler shadows the screen's for exactly as long
 * as the dialog is open — no flags to keep in sync.
 */
export function useBackHandler(handler: () => void, enabled = true): void {
  const { pushBackHandler } = useTvNavigation();
  const latest = useRef(handler);
  latest.current = handler;

  useEffect(() => {
    if (!enabled) return;
    return pushBackHandler(() => latest.current());
  }, [enabled, pushBackHandler]);
}

/**
 * Confines the D-pad to the returned element and pulls focus into it, restoring
 * the previously focused element on unmount.
 *
 * This is what stops a remote wandering out of an open dialog and pressing OK on
 * something hidden behind it.
 */
export function useFocusScope<T extends HTMLElement>(enabled = true): RefObject<T | null> {
  const { pushScope, focusFirst } = useTvNavigation();
  const ref = useRef<T>(null);

  useEffect(() => {
    const element = ref.current;
    if (!enabled || !element) return;

    const previous = document.activeElement as HTMLElement | null;
    const release = pushScope(element);
    focusFirst();

    return () => {
      release();
      // The element that opened the dialog is where the player expects to be.
      if (previous?.isConnected) previous.focus();
    };
  }, [enabled, focusFirst, pushScope]);

  return ref;
}

/**
 * Focuses the returned element once it exists.
 *
 * Every screen points this at its primary control, so arriving with a remote
 * leaves OK meaning something instead of nothing.
 */
export function useAutoFocus<T extends HTMLElement>(enabled = true): RefObject<T | null> {
  const ref = useRef<T>(null);

  useEffect(() => {
    if (enabled) ref.current?.focus();
  }, [enabled]);

  return ref;
}
